import { useContext } from 'react';

import { Flex, Text } from '@rover/kibble/core';
import type { HybridOnPressHandler } from '@rover/kibble/core/utils';
import StarRating from '@rover/kibble/patterns/StarRating';
import { ApiPersonDetailReviewsListServiceTypeCategory as ServiceTypeCategories } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListServiceTypeCategory';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { TrainerProfileReadAllReviewsClickedTop } from '../../../events/analyticsEvents';
import ReviewsModalContext from '../FullMixedReviewsList/ReviewsModalContext';

export interface RatingSummaryProps {
  ratingsAverage: number | null;
  reviewsCount: number;
  /** Which display width the component should be rendered for (and not rendered otherwise) */
  displayFor: 'compact' | 'full';
  providerOpk: string;
  hideReviewCountLink?: boolean;
  defaultCategory: ServiceTypeCategories;
}

export default function RatingSummary({
  ratingsAverage,
  reviewsCount,
  displayFor,
  providerOpk,
  hideReviewCountLink = false,
  defaultCategory,
}: RatingSummaryProps): JSX.Element {
  const display = displayFor === 'full' ? ['none', 'flex'] : ['flex', 'none'];
  const { openReviewsModal, setServiceTypeCategory } = useContext(ReviewsModalContext);
  const handleClickReviewsCount: HybridOnPressHandler = ({ webEvent: event }) => {
    emitAnalyticsEvent(new TrainerProfileReadAllReviewsClickedTop({ providerOpk }));
    setServiceTypeCategory(defaultCategory);
    openReviewsModal(event);
  };

  const textProps = hideReviewCountLink
    ? {
        fontWeight: 'regular' as const,
        textDecoration: 'none' as const,
        style: { cursor: 'default' },
      }
    : {
        fontWeight: 'semibold' as const,
        textDecoration: 'underline' as const,
        style: { cursor: 'pointer' },
        onPress: handleClickReviewsCount,
      };

  return (
    <Flex alignItems="center" display={display} sx={{ gap: '0.33em' }}>
      {ratingsAverage ? (
        <StarRating rating={ratingsAverage} variant="compact" data-testid="star-rating" />
      ) : null}
      <Text data-testid="review-count" {...textProps}>
        {/* TODO (DEV-104195): Translate message (replace with i18n.pluralize) */}({reviewsCount}{' '}
        {reviewsCount > 1 ? `reviews` : `review`} on Rover)
      </Text>
    </Flex>
  );
}
